import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaQuestionCircle } from 'react-icons/fa';
import PageTransition from '../components/UI/PageTransition';
import Navbar from '../components/Layout/Navbar';

const SECTIONS = [
  {
    icon: '⚽',
    title: 'Comment fonctionne le score ?',
    body: (
      <>
        <p className="mb-2">Chaque bonne réponse rapporte des points selon la difficulté de la question :</p>
        <ul className="list-disc pl-5 mb-2 space-y-1">
          <li>Facile : 100 pts</li>
          <li>Moyen : 200 pts</li>
          <li>Difficile : 300 pts</li>
        </ul>
        <p className="mb-2">S'ajoutent ensuite deux bonus :</p>
        <ul className="list-disc pl-5 mb-2 space-y-1">
          <li><strong>Rapidité</strong> : +50 pts si tu réponds en moins de 5s, +25 pts en moins de 10s</li>
          <li><strong>Série</strong> : +10% par bonne réponse d'affilée, jusqu'à +50% maximum (à partir de la 5e)</li>
        </ul>
        <p>Une mauvaise réponse retire 25 pts. Le tirage des questions est aléatoire (sauf en poule de tournoi, voir plus bas) — deux parties peuvent donc avoir des niveaux de difficulté différents.</p>
      </>
    ),
  },
  {
    icon: '🌟',
    title: "Comment monte-t-on de niveau, et qu'est-ce que le Prestige ?",
    body: (
      <>
        <p className="mb-2">Le niveau va de 1 à 150. Chaque niveau demande progressivement plus d'XP que le précédent — les premiers niveaux restent rapides, mais plus tu montes, plus il faut de parties pour passer au suivant.</p>
        <p className="mb-2">Une fois le niveau 150 atteint, un bouton <strong>Prestiger</strong> apparaît sur ton profil : ton niveau repart à 1, mais tu gagnes un badge permanent <strong>🌟1</strong>, <strong>🌟2</strong>... affiché à côté de ton niveau partout dans l'app.</p>
        <p>Aucune XP n'est perdue dans l'opération — c'est uniquement une nouvelle façon de montrer que tu as déjà dépassé le plafond, pour continuer à progresser au-delà du niveau 150.</p>
      </>
    ),
  },
  {
    icon: '👑',
    title: "Qu'est-ce que Premium m'apporte ?",
    body: (
      <ul className="list-disc pl-5 space-y-1.5">
        <li><strong>Vies illimitées</strong> en mode Chronométré (Survie)</li>
        <li><strong>Thèmes de quiz exclusifs</strong></li>
        <li><strong>Tournois et championnats réservés</strong> Premium (au cas par cas)</li>
        <li><strong>Badge couronne</strong> visible sur ton avatar partout dans l'app</li>
        <li><strong>Parrainage rémunéré</strong> (commission quand tes filleuls passent Premium)</li>
      </ul>
    ),
  },
  {
    icon: '🖼️',
    title: 'Comment fonctionnent les cadres d\'avatar ?',
    body: (
      <>
        <p className="mb-2">Dans la Boutique, section <strong>Cadres d'avatar</strong>, tu peux dépenser tes pièces pour personnaliser le contour de ton avatar — 5 cadres en 3 paliers (Basique, Rare, Prestige). C'est purement cosmétique : aucun effet sur ton score ou ton classement.</p>
        <p className="mb-2">Dès que tu achètes un cadre, il s'équipe automatiquement et devient visible partout (profil, classement, podiums) — pas besoin d'étape supplémentaire.</p>
        <p>Tu peux acheter plusieurs cadres : ils restent tous dans ton inventaire, et tu peux changer celui que tu portes à tout moment (bouton <strong>Équiper</strong>) sans frais supplémentaire. Pour revenir au cadre lié à ton niveau, choisis <strong>Aucun cadre</strong>.</p>
      </>
    ),
  },
  {
    icon: '📱',
    title: 'Le jeu bloque ou affiche une erreur bizarre ?',
    body: (
      <>
        <p className="mb-2">C'est presque toujours parce que ton téléphone a gardé une ancienne version de l'appli en mémoire. Essaie dans l'ordre :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>Ferme complètement l'appli (pas juste "retour" — depuis le gestionnaire d'applications récentes) puis rouvre-la</li>
          <li>Si ça persiste : vide le cache du site dans ton navigateur (Chrome : ⋮ → Paramètres des sites → quizfootleague.dekanisoftware.com → Supprimer les données)</li>
          <li>Toujours bloqué ? Signale-le avec le numéro de version affiché en bas de l'écran d'accueil ou de ton profil</li>
        </ol>
      </>
    ),
  },
  {
    icon: '🏆',
    title: 'Comment marchent les poules de tournoi ?',
    body: (
      <>
        <p className="mb-2">La phase de poules n'oppose pas directement les joueurs entre eux : chacun joue son quiz de 20 questions <strong>une seule fois</strong>, quand il veut, avant l'échéance fixée. C'est le score qui compte, pas la confrontation directe.</p>
        <p className="mb-2">Les <strong>2 premiers de chaque poule</strong> (au score, départagé par les bonnes réponses puis la rapidité) se qualifient pour la phase à élimination directe.</p>
        <p>En phase finale, les matchs sont des duels directs — pas de forfait automatique en cas de déconnexion, l'organisation tranche manuellement si besoin.</p>
      </>
    ),
  },
  {
    icon: '📊',
    title: 'Classements et records, comment ça marche ?',
    body: (
      <>
        <p className="mb-2">Le classement (Monde / Semaine / Mois / Amis) additionne les scores de <strong>toutes</strong> tes parties sur la période.</p>
        <p>L'onglet <strong>Records</strong> affiche le meilleur score jamais réalisé en une seule partie, par catégorie : Solo (10 questions), Poules de tournoi (20 questions) et Chronométré. Si ton record est battu, tu reçois une notification pour reprendre ta couronne.</p>
      </>
    ),
  },
];

function Guide() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <PageTransition variant="slideIn">
      <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '40px' }}>
        <Navbar title="GUIDE" showBack />

        <div className="flex items-center justify-center gap-2 pt-2 pb-4 px-4">
          <FaQuestionCircle size={18} className="text-qfl-gold" />
          <p className="text-white/60 text-sm text-center">
            Les réponses aux questions les plus fréquentes
          </p>
        </div>

        <div className="flex flex-col gap-3 px-4">
          {SECTIONS.map((section, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className="rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(240,180,41,0.2)' }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                >
                  <span className="text-xl flex-shrink-0">{section.icon}</span>
                  <span
                    className="flex-1 font-bold text-sm text-white"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    {section.title}
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    className="text-qfl-gold flex-shrink-0"
                  >
                    <FaChevronDown size={14} />
                  </motion.span>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="px-4 pb-4 text-white/70 text-sm leading-relaxed">
                        {section.body}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <p className="text-center text-white/30 text-xs mt-6 px-4">
          Une autre question ? Écris-nous : <a href="mailto:info@dekanisoftware.com" className="text-qfl-gold">info@dekanisoftware.com</a>
        </p>
      </div>
    </PageTransition>
  );
}

export default Guide;
